import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Input, InputEvent } from '@/components/form/Input';
import { Slider } from '@/components/Slider';
import { Button } from '@/components/ui/button';
import { RATE_LIMIT_FIXED_VALUES } from '@/config/rateLimitConfig';
import { useMy } from '@/hooks/useMy';
import { useUpdateSetting } from '@/hooks/useSetting';
import { queryClient } from '@/queryclient';
import { RATE_LIMIT_SYSTEM_DEFAULTS } from '@/sections/settings/constants';
import { ScanProfile } from '@/types/scanProfile';

const UNLIMITED_VALUE = 999999;

interface ScanProfilesFormProps {
  customerEmail?: string;
  onClose?: () => void;
}

interface ProfileFormState {
  id: string;
  name: string;
  simultaneousHosts: number;
  capabilityRateLimit: number;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function toFormState(
  profiles: ScanProfile[],
  globalHosts: number,
  globalRate: number
): ProfileFormState[] {
  return profiles.map(p => ({
    id: p.id,
    name: p.name,
    simultaneousHosts: p.simultaneousHosts ?? globalHosts,
    capabilityRateLimit: p.capabilityRateLimit ?? globalRate,
    // Use UNLIMITED_VALUE when the profile doesn't override the global
    ...(p.simultaneousHosts === undefined && {
      simultaneousHosts: UNLIMITED_VALUE,
    }),
    ...(p.capabilityRateLimit === undefined && {
      capabilityRateLimit: UNLIMITED_VALUE,
    }),
  }));
}

export const ScanProfilesForm: React.FC<ScanProfilesFormProps> = ({
  customerEmail,
  onClose,
}) => {
  const { data, status } = useMy(
    { resource: 'setting' },
    customerEmail ? { email: customerEmail } : undefined
  );
  const { mutateAsync: updateSetting, status: updateStatus } =
    useUpdateSetting(customerEmail);

  // Read the current global rate limit to show in "Use Global Setting" labels
  const globalRateLimit = useMemo(() => {
    const setting = data?.find(s => s.name === 'rate-limit')?.value as
      | { simultaneousHosts?: number; capabilityRateLimit?: number }
      | undefined;
    return {
      simultaneousHosts:
        setting?.simultaneousHosts ??
        RATE_LIMIT_SYSTEM_DEFAULTS.SIMULTANEOUS_HOSTS,
      capabilityRateLimit:
        setting?.capabilityRateLimit ??
        RATE_LIMIT_SYSTEM_DEFAULTS.REQUESTS_PER_HOST,
    };
  }, [data]);

  const apiProfiles = useMemo(() => {
    const setting = data?.find(s => s.name === 'scan-profiles');
    return (setting?.value as ScanProfile[] | undefined) ?? [];
  }, [data]);

  const initialState = useMemo(
    () =>
      toFormState(
        apiProfiles,
        globalRateLimit.simultaneousHosts,
        globalRateLimit.capabilityRateLimit
      ),
    [apiProfiles, globalRateLimit]
  );

  // null = user hasn't edited yet, show API state
  const [localProfiles, setLocalProfiles] = useState<ProfileFormState[] | null>(
    null
  );

  const profiles = localProfiles ?? initialState;

  const setProfiles = (
    updater:
      | ProfileFormState[]
      | ((prev: ProfileFormState[]) => ProfileFormState[])
  ) => {
    if (typeof updater === 'function') {
      setLocalProfiles(prev => updater(prev ?? initialState));
    } else {
      setLocalProfiles(updater);
    }
  };

  const handleAddProfile = () => {
    setProfiles(prev => [
      ...prev,
      {
        id: '',
        name: '',
        simultaneousHosts: UNLIMITED_VALUE,
        capabilityRateLimit: UNLIMITED_VALUE,
      },
    ]);
  };

  const handleRemoveProfile = (index: number) => {
    setProfiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateProfile = (
    index: number,
    field: keyof ProfileFormState,
    value: string | number
  ) => {
    setProfiles(prev =>
      prev.map((p, i) => {
        if (i !== index) return p;
        const updated = { ...p, [field]: value };
        if (field === 'name' && typeof value === 'string') {
          updated.id = slugify(value);
        }
        return updated;
      })
    );
  };

  const handleApply = async () => {
    const invalidProfile = profiles.find(p => !p.name.trim() || !p.id);
    if (invalidProfile) {
      toast.error('All profiles must have a name');
      return;
    }

    const ids = profiles.map(p => p.id);
    if (new Set(ids).size !== ids.length) {
      toast.error('Profile names must be unique');
      return;
    }

    try {
      const value: ScanProfile[] = profiles.map(p => ({
        id: p.id,
        name: p.name,
        ...(p.simultaneousHosts !== UNLIMITED_VALUE && {
          simultaneousHosts: p.simultaneousHosts,
        }),
        ...(p.capabilityRateLimit !== UNLIMITED_VALUE && {
          capabilityRateLimit: p.capabilityRateLimit,
        }),
      }));

      await updateSetting({
        name: 'scan-profiles',
        value,
        silent: true,
      });

      // Explicitly refetch settings to ensure the card updates before modal closes
      await queryClient.refetchQueries({
        predicate: query => {
          const qk = query.queryKey as string[];
          return qk[0] === 'MY' && qk[1] === 'setting';
        },
      });

      toast.success('Scan profiles updated');
      onClose?.();
    } catch (e) {
      toast.error('Failed to update scan profiles', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  const isLoading = status === 'pending' || updateStatus === 'pending';
  const isReady = status === 'success';

  return (
    <div className="flex flex-col gap-8" data-testid="scan-profiles-form">
      <div className="flex flex-col gap-6">
        {profiles.map((profile, index) => (
          <div
            key={index}
            className="border-chariot-stroke flex flex-col gap-4 rounded-lg border p-6"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Input
                  name={`profile-name-${index}`}
                  label="Profile Name"
                  value={profile.name}
                  onChange={(e: InputEvent) =>
                    handleUpdateProfile(index, 'name', e.target.value)
                  }
                  placeholder="e.g., Sensitive Sites"
                />
                {profile.id && (
                  <span className="text-chariot-text-tertiary mt-1 text-xs">
                    ID: {profile.id}
                  </span>
                )}
              </div>
              <button
                onClick={() => handleRemoveProfile(index)}
                className="text-chariot-text-secondary hover:text-chariot-status-critical mt-6 cursor-pointer p-1 transition-colors"
                aria-label={`Remove profile ${profile.name}`}
                type="button"
              >
                <TrashIcon className="size-4" />
              </button>
            </div>

            <div className="space-y-8">
              <div className="border-chariot-stroke border-b pb-8">
                <Slider
                  min={RATE_LIMIT_FIXED_VALUES.SLOW_SCAN_SIMULTANEOUS_HOSTS}
                  max={RATE_LIMIT_FIXED_VALUES.FULL_RATE_SIMULTANEOUS_HOSTS}
                  value={profile.simultaneousHosts}
                  onChange={v =>
                    handleUpdateProfile(index, 'simultaneousHosts', v)
                  }
                  enableUnlimited={true}
                  unlimitedLabel={`Use Global Setting (${globalRateLimit.simultaneousHosts})`}
                  title="Maximum simultaneous hosts scanned"
                  subtitle="Controls how many assets with this profile are scanned in parallel."
                  data-testid={`scan-profile-hosts-${index}`}
                />
              </div>
              <div>
                <Slider
                  min={RATE_LIMIT_FIXED_VALUES.SLOW_SCAN_REQUESTS_PER_HOST}
                  max={RATE_LIMIT_FIXED_VALUES.FULL_RATE_REQUESTS_PER_HOST}
                  value={profile.capabilityRateLimit}
                  onChange={v =>
                    handleUpdateProfile(index, 'capabilityRateLimit', v)
                  }
                  enableUnlimited={true}
                  unlimitedLabel={`Use Global Setting (${globalRateLimit.capabilityRateLimit} rq/s)`}
                  title="Maximum rate of requests per host"
                  subtitle="Controls scanning intensity per individual asset with this profile."
                  unit="rq/s"
                  data-testid={`scan-profile-rate-${index}`}
                />
              </div>
            </div>
          </div>
        ))}

        {profiles.length === 0 && isReady && (
          <div className="border-chariot-stroke flex flex-col gap-4 rounded-lg border p-6">
            <div className="flex flex-col gap-2">
              <h4 className="text-chariot-text-primary font-semibold">
                No scan profiles configured
              </h4>
              <p className="text-chariot-text-secondary text-sm">
                Scan profiles let you set custom rate limits for specific assets
                or seeds. Profile limits are always capped at the global rate
                limit — they can reduce scanning intensity for sensitive
                targets, but never exceed the global maximum.
              </p>
            </div>
            <div className="text-chariot-text-secondary space-y-2 text-sm">
              <p className="font-medium">How it works:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>
                  Create a profile here, then assign it to assets or seeds via
                  the{' '}
                  <span className="font-medium">
                    Manage → Change Scan Profile
                  </span>{' '}
                  menu in any asset or seed table/drawer.
                </li>
                <li>
                  Any asset type (domains, IPs, web apps) can be assigned a
                  profile. Seeds propagate their profile to discovered child
                  assets automatically.
                </li>
                <li>
                  Unassigned assets continue to use the global rate limit.
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={handleAddProfile}
          disabled={isLoading}
          data-testid="scan-profiles-add-button"
        >
          <PlusIcon className="size-4" />
          Add Profile
        </Button>
        <div className="flex gap-2">
          {onClose && (
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={isLoading}
              data-testid="scan-profiles-cancel-button"
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={handleApply}
            disabled={isLoading || !isReady}
            data-testid="scan-profiles-apply-button"
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
};
